import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useOfflineStatus } from "../lib/offline/offlineApi";
import { Bell, MessageSquare, Download, WifiOff, Wifi, CheckSquare, Clock, GraduationCap, Wallet, FolderOpen, Smartphone, Settings, Package, Receipt } from "lucide-react";

const MODULES = [
  { to: "/salarie/notifications", icon: Bell, label: "Notifications", desc: "Alertes et messages système", key: "notifs" },
  { to: "/salarie/messages", icon: MessageSquare, label: "Messagerie", desc: "Photos, vidéos, documents", key: "messages" },
  { to: "/salarie/taches", icon: CheckSquare, label: "Mes tâches", desc: "À faire, même hors ligne" },
  { to: "/salarie/bulletins", icon: Wallet, label: "Mes bulletins", desc: "Bulletins de paie" },
  { to: "/salarie/documents", icon: FolderOpen, label: "Mes documents", desc: "Documents personnels" },
  { to: "/salarie/pointages", icon: Clock, label: "Pointages", desc: "Heures et présence", perm: "time_tracking.view" },
  { to: "/salarie/depenses", icon: Receipt, label: "Notes de frais", desc: "Dépenses à rembourser" },
  { to: "/salarie/equipement", icon: Package, label: "Mon équipement", desc: "Matériel attribué" },
  { to: "/formateur/sessions", icon: GraduationCap, label: "Formation", desc: "Sessions et modules (hors ligne)", perm: "formation.view" },
];

export default function SalarieHomePage() {
  const { user, axiosAuth } = useAuth();
  const status = useOfflineStatus();
  const [counts, setCounts] = useState({ notifs: 0, messages: 0 });
  const [installEvt, setInstallEvt] = useState(null);
  const perms = user?.module_permissions || {};
  const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

  useEffect(() => {
    const bip = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", bip);
    axiosAuth().get("/notifications/counts").then((r) => setCounts({ notifs: r.data.total || 0, messages: r.data.messages || 0 })).catch(() => {});
    return () => window.removeEventListener("beforeinstallprompt", bip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = MODULES.filter((m) => !m.perm || perms[m.perm] || ["admin", "superadmin", "tenant_admin"].includes(user?.role));
  return (
    <div className="p-4 sm:p-8" data-testid="salarie-home">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-salarie-192.png" alt="" className="w-11 h-11 rounded-xl" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#f7c935] font-black">Blitzz Salarié</div>
              <h1 className="text-2xl sm:text-3xl font-black leading-tight">Bonjour {user?.name?.split(" ")[0] || ""}</h1>
            </div>
          </div>
          <span className={`text-[11px] px-2 py-1 rounded-full flex items-center gap-1 ${status.online ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid="salarie-online-badge">
            {status.online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />} {status.online ? "En ligne" : "Hors ligne — données en cache"}
          </span>
        </div>

        {!standalone && (
          <div className="mt-5 rounded-2xl border border-[#f7c935]/30 bg-[#f7c935]/10 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="salarie-install-banner">
            <div className="text-xs text-slate-200 flex items-center gap-2"><Smartphone className="w-4 h-4 text-[#f7c935]" /> Installez l'app sur votre téléphone ou ordinateur : notifications et mode hors ligne.</div>
            <div className="flex gap-2">
              {installEvt && <button onClick={async () => { installEvt.prompt(); setInstallEvt(null); }} className="px-3 py-1.5 rounded-lg bg-[#f7c935] text-[#0b0811] text-xs font-bold flex items-center gap-1" data-testid="salarie-install-btn"><Download className="w-3.5 h-3.5" /> Installer</button>}
              <Link to="/salarie/app" className="px-3 py-1.5 rounded-lg bg-white/10 text-xs text-white" data-testid="salarie-installers-link">Windows / Android / Mac</Link>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
          {visible.map((m) => {
            const badge = m.key ? counts[m.key] : 0;
            return (
              <Link key={m.to} to={m.to} className="rounded-2xl border border-white/10 bg-[#14101c] p-4 hover:border-[#f7c935]/50 transition-colors relative" data-testid={`salarie-module-${m.to.replace(/\//g, "-").slice(1)}`}>
                <m.icon className="w-5 h-5 text-[#f7c935]" />
                <div className="text-sm font-bold mt-3">{m.label}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{m.desc}</div>
                {badge > 0 && <span className="absolute top-3 right-3 text-[10px] font-black bg-[#f97316] text-[#0b0811] rounded-full px-1.5 py-0.5" data-testid={`salarie-badge-${m.key}`}>{badge}</span>}
              </Link>
            );
          })}
        </div>

        <Link to="/salarie/reglages" className="mt-6 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#14101c] p-4 hover:border-[#f7c935]/50 transition-colors" data-testid="salarie-settings-link">
          <Settings className="w-5 h-5 text-[#f7c935]" />
          <div className="flex-1">
            <div className="text-sm font-bold">Notifications & hors ligne</div>
            <div className="text-[11px] text-slate-400">Activer les notifications push, choisir les catégories, préparer les données hors ligne.</div>
          </div>
          {status.pending > 0 && <span className="text-[10px] text-amber-300 font-bold" data-testid="salarie-pending-count">{status.pending} en attente</span>}
        </Link>
      </div>
    </div>
  );
}
